import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert20Regular,
  CheckmarkCircle20Regular,
  Delete20Regular,
  Mail20Regular,
  MoneyHand20Regular,
  ReceiptBag20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import { formatCurrency, formatDateShort, formatDateTime } from "../Obras/obraFormat";
import {
  deleteRentNotice,
  generateRentNotices,
  getRentNotices,
  sendRentNoticeEmail,
  setRentNoticePaid,
  type RentNotice,
} from "../../rendas/rendasApi";
import {
  getRentCollectionActivities,
  sendRentCollectionEmail,
  type RentCollectionActivity,
} from "../../rendas/rentCollectionsApi";
import { generateRentReceipt } from "../../recibos/recibosApi";

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// Cor por estado do aviso — classes já compiladas em index.css (grep-verificadas).
// Classificador é o statusCode ("pendente" | "paga" | "atrasada"); fallback neutro.
const STATUS_BADGE: Record<string, string> = {
  pendente: "bg-yellow-500/10 text-yellow-700",
  paga: "bg-green-500/10 text-green-700",
  atrasada: "bg-destructive/10 text-destructive",
};

const statusBadgeClass = (statusCode: string): string =>
  STATUS_BADGE[statusCode] ?? "bg-muted text-secondary-text";

const STATUS_LABEL: Record<string, string> = {
  pendente: "Pendente",
  paga: "Paga",
  atrasada: "Atrasada",
};

const statusLabel = (statusCode: string): string =>
  STATUS_LABEL[statusCode] ?? statusCode;

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const STATUS_CHIPS = [
  { code: "", name: "Todos" },
  { code: "pendente", name: "Pendente" },
  { code: "paga", name: "Paga" },
  { code: "atrasada", name: "Atrasada" },
];

// Fases de escalada do workflow de cobrança [5.4.2] — pela ordem de escalada.
const COLLECTION_STAGES: { code: string; label: string }[] = [
  { code: "due_notice", label: "Aviso de vencimento" },
  { code: "soft", label: "Cobrança suave" },
  { code: "firm", label: "Cobrança firme" },
];

// Dias inteiros de atraso (data de vencimento → hoje). 0 se ainda não vencida/ inválida.
const daysOverdue = (dueDate: string): number => {
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const now = new Date();
  const dueMid = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((todayMid.getTime() - dueMid.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
};

type ToastKind = "success" | "error";

export function RendasView() {
  const now = new Date();
  const currentYear = now.getFullYear();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [notices, setNotices] = useState<RentNotice[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(
    null,
  );
  const toastTimeoutRef = useRef<number | null>(null);

  const showToast = useCallback((kind: ToastKind, message: string) => {
    setToast({ kind, message });
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 4000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    },
    [],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRentNotices({ year, month });
      setNotices(data);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível carregar os avisos de renda."));
    } finally {
      setIsLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const result = await generateRentNotices(year, month);
      await load();
      const message =
        result.skipped > 0
          ? `${result.created} aviso(s) gerado(s) · ${result.skipped} ignorado(s)`
          : `${result.created} aviso(s) gerado(s)`;
      showToast("success", message);
    } catch (err) {
      showToast("error", errorMessage(err, "Não foi possível gerar os avisos."));
    } finally {
      setIsGenerating(false);
    }
  }, [year, month, load, showToast]);

  const handleTogglePaid = useCallback(
    async (notice: RentNotice) => {
      setBusyId(notice.id);
      try {
        const updated = await setRentNoticePaid(notice.id, !notice.isPaid);
        setNotices((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
        showToast(
          "success",
          updated.isPaid ? "Renda marcada como paga." : "Renda marcada como pendente.",
        );
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível atualizar o estado."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  const handleSendEmail = useCallback(
    async (notice: RentNotice) => {
      setBusyId(notice.id);
      try {
        const updated = await sendRentNoticeEmail(notice.id);
        setNotices((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
        showToast("success", "Aviso enviado por email.");
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível enviar o aviso."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  // Gera (ou recupera — idempotente) o rascunho de recibo de uma renda paga [5.3.2].
  const handleGenerateReceipt = useCallback(
    async (notice: RentNotice) => {
      setBusyId(notice.id);
      try {
        const receipt = await generateRentReceipt(notice.id);
        showToast("success", `Recibo ${receipt.receiptNumber} pronto (ver Recibos).`);
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível gerar o recibo."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  const handleDelete = useCallback(
    async (notice: RentNotice) => {
      setBusyId(notice.id);
      try {
        await deleteRentNotice(notice.id);
        setNotices((prev) => prev.filter((n) => n.id !== notice.id));
        setConfirmDeleteId(null);
        showToast("success", "Aviso apagado.");
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível apagar o aviso."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  // Workflow de cobrança de rendas em atraso [5.4.2] ------------------------
  const [collectionNotice, setCollectionNotice] = useState<RentNotice | null>(
    null,
  );
  const [activities, setActivities] = useState<RentCollectionActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [sendingStage, setSendingStage] = useState<string | null>(null);
  const [collectionError, setCollectionError] = useState<string | null>(null);

  const loadActivities = useCallback(async (noticeId: number) => {
    setIsLoadingActivities(true);
    setCollectionError(null);
    try {
      setActivities(await getRentCollectionActivities(noticeId));
    } catch (err) {
      setCollectionError(
        errorMessage(err, "Não foi possível carregar o histórico de cobrança."),
      );
    } finally {
      setIsLoadingActivities(false);
    }
  }, []);

  const openCollection = useCallback(
    (notice: RentNotice) => {
      setCollectionNotice(notice);
      setActivities([]);
      setSendingStage(null);
      setCollectionError(null);
      loadActivities(notice.id);
    },
    [loadActivities],
  );

  const handleSendStage = useCallback(
    async (stage: string) => {
      if (!collectionNotice || sendingStage !== null) return;
      setSendingStage(stage);
      setCollectionError(null);
      try {
        await sendRentCollectionEmail(collectionNotice.id, stage);
        await loadActivities(collectionNotice.id);
        showToast("success", "Ação de cobrança enviada");
      } catch (err) {
        setCollectionError(
          errorMessage(err, "Não foi possível enviar a ação de cobrança."),
        );
      } finally {
        setSendingStage(null);
      }
    },
    [collectionNotice, sendingStage, loadActivities, showToast],
  );

  const normalizedFilter = filter.trim().toLowerCase();
  const shownNotices = useMemo(() => {
    return notices.filter((n) => {
      if (statusFilter !== "" && n.statusCode !== statusFilter) return false;
      if (normalizedFilter === "") return true;
      return [n.propertyName, n.tenantName, n.periodLabel, n.contractNumber ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedFilter);
    });
  }, [notices, statusFilter, normalizedFilter]);

  // Acompanhamento de pagamento [5.4.1] — conta sobre a lista carregada do período.
  const overdueCount = useMemo(
    () => notices.filter((n) => n.isOverdue).length,
    [notices],
  );

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y += 1) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Rendas"
        subtitle="Avisos de renda e acompanhamento de pagamento"
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {overdueCount > 0 && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {overdueCount} renda(s) em atraso.
          </div>
        )}

        {/* Controlos: período (ano/mês) + gerar avisos do mês [5.3.1] */}
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Ano
            <select
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Mês
            <select
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderRadius: "var(--radius-button)",
              height: "var(--button-height)",
            }}
          >
            <MoneyHand20Regular className="w-5 h-5" />
            {isGenerating ? "A gerar…" : "Gerar avisos do mês"}
          </button>
        </div>

        <ListToolbar
          filter={filter}
          onFilterChange={setFilter}
          placeholder="Filtrar por imóvel, inquilino ou período…"
          summary={
            <span className="text-secondary-text text-sm">
              {shownNotices.length} aviso(s)
            </span>
          }
        />

        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_CHIPS.map((chip) => {
            const isActive = statusFilter === chip.code;
            return (
              <button
                key={chip.code || "all"}
                type="button"
                onClick={() => setStatusFilter(chip.code)}
                className={`inline-flex items-center px-3 py-1 text-sm cursor-pointer ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent/50"
                }`}
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {chip.name}
              </button>
            );
          })}
        </div>

        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 960 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3">Imóvel</th>
                <th className="px-4 py-3">Inquilino</th>
                <th className="px-4 py-3" style={{ width: 140 }}>
                  Período
                </th>
                <th className="px-4 py-3" style={{ width: 120 }}>
                  Vencimento
                </th>
                <th className="px-4 py-3 text-right" style={{ width: 120 }}>
                  Valor
                </th>
                <th className="px-4 py-3" style={{ width: 120 }}>
                  Estado
                </th>
                <th className="px-4 py-3" style={{ width: 280 }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    A carregar…
                  </td>
                </tr>
              ) : shownNotices.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    {filter !== "" || statusFilter !== ""
                      ? "Sem avisos para o filtro."
                      : "Sem avisos de renda para o período. Gere os avisos do mês."}
                  </td>
                </tr>
              ) : (
                shownNotices.map((n) => {
                  const isBusy = busyId === n.id;
                  return (
                    <tr
                      key={n.id}
                      className="border-b border-border last:border-b-0 hover:bg-accent/30"
                    >
                      <td
                        className="px-4 py-3 break-words"
                        style={{ fontWeight: "var(--font-weight-medium)" }}
                      >
                        {n.propertyName}
                        {n.contractNumber && (
                          <span className="block text-xs text-secondary-text mt-0.5">
                            Nº {n.contractNumber}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 break-words">{n.tenantName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {n.periodLabel}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateShort(n.dueDate)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatCurrency(n.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs ${statusBadgeClass(
                            n.statusCode,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {statusLabel(n.statusCode)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {confirmDeleteId === n.id ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-secondary-text">
                              Apagar?
                            </span>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => handleDelete(n)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Confirmar
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmDeleteId(null)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => handleTogglePaid(n)}
                              className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                            >
                              <CheckmarkCircle20Regular className="w-4 h-4" />
                              {n.isPaid ? "Marcar pendente" : "Marcar paga"}
                            </button>
                            {n.notifiedByEmail ? (
                              <span className="inline-flex items-center gap-1 text-green-700 text-sm">
                                <CheckmarkCircle20Regular className="w-4 h-4" />
                                Enviado
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => handleSendEmail(n)}
                                className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                              >
                                <Mail20Regular className="w-4 h-4" />
                                Enviar aviso
                              </button>
                            )}
                            {!n.isPaid && (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => openCollection(n)}
                                className={`inline-flex items-center gap-1 text-sm cursor-pointer disabled:opacity-50 ${
                                  n.isOverdue ? "text-destructive" : "text-primary"
                                }`}
                              >
                                <Alert20Regular className="w-4 h-4" />
                                Cobrança
                              </button>
                            )}
                            {n.isPaid && (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => handleGenerateReceipt(n)}
                                className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                              >
                                <ReceiptBag20Regular className="w-4 h-4" />
                                Gerar recibo
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmDeleteId(n.id)}
                              className="inline-flex items-center gap-1 text-destructive text-sm cursor-pointer disabled:opacity-50"
                            >
                              <Delete20Regular className="w-4 h-4" />
                              Apagar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {collectionNotice && (
        <Modal
          title={`Cobrança de renda — ${collectionNotice.periodLabel}`}
          onClose={() => setCollectionNotice(null)}
          maxWidthClass="max-w-2xl"
          footer={
            <button
              type="button"
              onClick={() => setCollectionNotice(null)}
              className="px-4 py-2 min-h-[44px] border border-border"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Fechar
            </button>
          }
        >
          {collectionError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {collectionError}
            </div>
          )}

          {/* Resumo do aviso */}
          <div
            className="border border-border bg-muted p-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            <div>
              <span className="text-secondary-text">Imóvel</span>
              <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                {collectionNotice.propertyName}
              </p>
            </div>
            <div>
              <span className="text-secondary-text">Inquilino</span>
              <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                {collectionNotice.tenantName}
              </p>
            </div>
            <div>
              <span className="text-secondary-text">Período</span>
              <p>{collectionNotice.periodLabel}</p>
            </div>
            <div>
              <span className="text-secondary-text">Valor</span>
              <p>{formatCurrency(collectionNotice.amount)}</p>
            </div>
            <div>
              <span className="text-secondary-text">Vencimento</span>
              <p>{formatDateShort(collectionNotice.dueDate)}</p>
            </div>
            <div>
              <span className="text-secondary-text">Dias em atraso</span>
              <p
                className={
                  collectionNotice.isOverdue ? "text-destructive" : undefined
                }
                style={{ fontWeight: "var(--font-weight-medium)" }}
              >
                {daysOverdue(collectionNotice.dueDate)}
              </p>
            </div>
          </div>

          {/* Botões de escalada */}
          <div className="space-y-2">
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Enviar ação de cobrança por email:
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {COLLECTION_STAGES.map((stage) => (
                <button
                  key={stage.code}
                  type="button"
                  disabled={sendingStage !== null}
                  onClick={() => handleSendStage(stage.code)}
                  className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] border border-border text-foreground hover:bg-accent/50 cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  <Mail20Regular className="w-4 h-4" />
                  {sendingStage === stage.code ? "A enviar…" : stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Histórico de ações */}
          <div className="space-y-2">
            <p style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
              Histórico de cobrança
            </p>
            {isLoadingActivities ? (
              <p className="text-sm text-secondary-text py-2">A carregar…</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-secondary-text py-2">
                Sem ações de cobrança registadas para este aviso.
              </p>
            ) : (
              <ul className="space-y-3">
                {activities.map((activity) => (
                  <li
                    key={activity.id}
                    className="border border-border p-3"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                        {activity.stageName} · {activity.channelName}
                      </span>
                      <span className="text-xs text-secondary-text">
                        {formatDateTime(activity.activityDate)}
                      </span>
                    </div>
                    {activity.subject && (
                      <p className="text-sm mt-1">{activity.subject}</p>
                    )}
                    {activity.outcome && (
                      <p className="text-sm text-secondary-text mt-1 whitespace-pre-wrap">
                        {activity.outcome}
                      </p>
                    )}
                    {activity.emailTo && (
                      <p className="text-xs text-secondary-text mt-1">
                        Para: {activity.emailTo}
                        {activity.emailStatus ? ` · ${activity.emailStatus}` : ""}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}

      {toast && (
        <div
          className={`fixed border border-border px-4 py-3 text-sm ${
            toast.kind === "success"
              ? "bg-green-500/10 text-green-700"
              : "bg-destructive/10 text-destructive"
          }`}
          style={{
            bottom: "1.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            borderRadius: "var(--radius)",
          }}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
